import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Form } from 'react-form';
import { parsePhoneNumber } from 'react-phone-number-input';
import moment from 'moment';
import * as queryString from 'query-string';

import { getApiAxiosInstance } from '../../utils/network';
import { getApiErrorMessage } from '../../utils/apiError';
import {
    Input,
    Location,
    PhoneNumber,
    Select,
} from '../Form';
import { createReactFormValidator, required } from '../../utils/validation';
import { intlPhoneNumber } from '../../utils/validation-libdep';
import Popup, { PopupTypes } from '../AdditionalInfoForm/PopupV2';
import { showErrorPopup } from '../../actions/notification-actions';
import { getYearOptions, getDayOptions, months, daysPerMonth } from '../../utils/calendar';

import config from '../../../config';
import GenderField from './GenderField';
import styles from './oauthConfirmationPage.scss';

const OAuthConfirmationPage = ({ providerConfig }) => {
    const { testIdPrefix, getUserProfile, confirmUser, idFieldName, testIds } = providerConfig;

    const location = useLocation();
    const history = useHistory();
    const { state } = queryString.parse(location.search);
    const dispatch = useDispatch();

    const [isLoading, setIsLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [utrMember, setUtrMember] = useState(null);
    const [checked, setChecked] = useState(false);
    const [days, setDays] = useState(31);
    const [showPopup, setShowPopup] = useState(false);
    const [popUpType, setPopUpType] = useState(null);
    const [isLocationChosenFromDropdown, setIsLocationChosenFromDropdown] = useState(false);
    const [belowAge, setBelowAge] = useState(false);
    const [selectedMonth, setSelectedMonth] = useState(null);
    const [selectedDay, setSelectedDay] = useState(null);
    const [selectedYear, setSelectedYear] = useState(null);

    useEffect(() => {
        const getProfileConfirmation = async () => {
            setIsLoading(true);

            try {
                const axiosApiInstance = getApiAxiosInstance();
                const response = await getUserProfile(axiosApiInstance, state);

                if (response?.data) {
                    setUtrMember(response?.data);
                    // If location is provided, mark it as chosen from dropdown
                    if (response.data.location) {
                        setIsLocationChosenFromDropdown(true);
                    }
                } else {
                    // Fallback to empty data if API returns empty
                    setUtrMember({
                        firstName: null,
                        lastName: null,
                        email: '',
                        [idFieldName]: null,
                        location: '',
                        mobileNumber: '',
                        month: null,
                        day: null,
                        year: null,
                        gender: '',
                    });
                }
                setIsLoading(false);
            } catch {
                setIsLoading(false);
                dispatch(showErrorPopup({
                    error: 'Invalid authentication state',
                    onConfirmClick: () => history.push('/login'),
                    onCancelClick: () => history.push('/login'),
                }));
            }
        };

        if (state) {
            getProfileConfirmation();
        } else {
            dispatch(showErrorPopup({
                error: 'Invalid authentication state',
                onConfirmClick: () => history.push('/login'),
                onCancelClick: () => history.push('/login'),
            }));
        }
    }, []);

    const handleOnTermsCheckChange = () => {
        setChecked(!checked);
    };

    const checkAge = (monthVal, dayVal, yearVal) => {
        if (monthVal && dayVal && yearVal) {
            const monthIndex = months.indexOf(monthVal);
            if (monthIndex >= 0) {
                const date = moment().year(yearVal).month(monthIndex).date(dayVal);
                const diffInYears = moment().diff(date, 'years');
                if (diffInYears <= 13) {
                    setBelowAge(true);
                } else {
                    setBelowAge(false);
                }
            }
        }
    };

    const handleMonthChange = (val) => {
        const monthIndex = months.indexOf(val);
        if (monthIndex !== -1) {
            setDays(daysPerMonth[monthIndex]);
        }
        setSelectedMonth(val);
        checkAge(val, selectedDay, selectedYear);
    };

    const handleDayChange = (val) => {
        setSelectedDay(val);
        checkAge(selectedMonth, val, selectedYear);
    };

    const handleYearChange = (val) => {
        setSelectedYear(val);
        checkAge(selectedMonth, selectedDay, val);
    };

    const togglePopup = (ev, type) => {
        if (ev) {
            ev.preventDefault();
        }
        setShowPopup(!showPopup);
        setPopUpType(type);
    };

    const handleAutocomplete = () => {
        setIsLocationChosenFromDropdown(true);
    };

    const handleLocationChange = () => {
        setIsLocationChosenFromDropdown(false);
    };

    const validateLocation = () => {
        if (!isLocationChosenFromDropdown) {
            return 'Please enter a valid location';
        }
    };

    const handleSubmit = async (values) => {
        setSubmitting(true);

        // Convert month, day, year into a DateTime
        const monthIndex = months.indexOf(values.month);
        const birthDate = new Date(values.year, monthIndex, values.day).toISOString();

        const payload = {
            firstName: values.firstName,
            lastName: values.lastName,
            email: values.email,
            [idFieldName]: utrMember[idFieldName],
            location: values.location,
            phoneNumber: values.phone,
            phoneCountry: parsePhoneNumber(values.phone)?.country || 'US',
            birthDate,
            sex: values.gender,
        };

        try {
            const axiosApiInstance = getApiAxiosInstance();
            const response = await confirmUser(axiosApiInstance, state, payload);

            if (response?.data?.redirectTo) {
                window.location.href = response?.data?.redirectTo;
            }
            setSubmitting(false);
        } catch (error) {
            setSubmitting(false);

            // Display the error message from the API
            const errorMessage = getApiErrorMessage(error);
            if (errorMessage) {
                dispatch(showErrorPopup({ error: errorMessage }));
            }
        }
    };

    const years = getYearOptions();
    const dayValues = getDayOptions(days);

    const inputProps = {
        placeholder: 'Location (zip or city)',
    };

    if (isLoading || !utrMember) {
        return (
            <div className="container d-flex jcc">
                <div className="d-flex jcc mt48">
                    <span data-testid={`${testIdPrefix}.loading`}>
                        <i className="fas fa-circle-notch fa-spin" />{' '}
                        Loading...
                    </span>
                </div>
            </div>
        );
    }

    const defaultValues = {
        firstName: utrMember.firstName || '',
        lastName: utrMember.lastName || '',
        email: utrMember.email || '',
        location: utrMember.location || '',
        phone: utrMember.mobileNumber || '',
        month: utrMember.month || null,
        day: utrMember.day || null,
        year: utrMember.year || null,
        gender: utrMember.gender || '',
    };

    return (
        <div className="container d-flex jcc">
            <div className={styles.wrapper} data-testid={`${testIdPrefix}.wrapper`}>
                <div className={styles.title} data-testid={`${testIdPrefix}.title`}>
                    Player Profile Information
                </div>

                <Form
                    onSubmit={handleSubmit}
                    defaultValues={defaultValues}
                >
                    {(formApi) => {
                        const { submitForm, values, touched, getWarning, getError } = formApi;

                        return (
                            <form onSubmit={submitForm} data-testid={`${testIdPrefix}.form`}>
                                <div className="row">
                                    <div className="col-12">
                                        <label className={'fw500 fs16 mb8'} data-testid={`${testIdPrefix}.instructions-label`}>
                                            Please fill out the following:
                                        </label>
                                        <label className={'mb24'} data-testid={`${testIdPrefix}.guardian-label`}>
                                            If you are the player&apos;s parent/guardian and creating this account for them, please use their player {` `}
                                            <span className="dib" data-testid={testIds.guardianSpan}>details.{` `}
                                                <a
                                                    className="link clickable"
                                                    onClick={(ev) => { togglePopup(ev, PopupTypes.PLAYER_DETAILS); }}
                                                    data-testid={`${testIdPrefix}.player-details-link`}
                                                >
                                                    <img src="/img/v4/info-icon.svg" width={12} alt="info" data-testid={testIds.playerDetailsImg} />
                                                </a>
                                            </span>
                                        </label>
                                        <Input
                                            id="firstNameInput"
                                            field="firstName"
                                            label="First Name"
                                            validate={createReactFormValidator(required)}
                                            themeV3
                                            data-testid={`${testIdPrefix}.first-name-input`}
                                        />
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-12">
                                        <Input
                                            id="lastNameInput"
                                            field="lastName"
                                            label="Last Name"
                                            validate={createReactFormValidator(required)}
                                            themeV3
                                            data-testid={`${testIdPrefix}.last-name-input`}
                                        />
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-12">
                                        <Input
                                            id="emailInput"
                                            field="email"
                                            label="Email"
                                            validate={createReactFormValidator(required)}
                                            disabled={!!utrMember?.email}
                                            themeV3
                                            data-testid={`${testIdPrefix}.email-input`}
                                        />
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-12">
                                        <Location
                                            label="Location (zip or city)"
                                            id="location"
                                            field="location"
                                            inputProps={inputProps}
                                            searchOptions={{ types: ['(regions)'] }}
                                            handleSelect={handleAutocomplete}
                                            handleChange={handleLocationChange}
                                            validate={validateLocation}
                                            themeV3
                                            data-testid={testIds.location}
                                        />
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-12">
                                        <PhoneNumber
                                            label="Phone Number"
                                            id="phone"
                                            field="phone"
                                            validate={createReactFormValidator([required, intlPhoneNumber])}
                                            defaultCountry="US"
                                            themeV3
                                            data-testid={`${testIdPrefix}.phone-input`}
                                        />
                                    </div>
                                </div>

                                <GenderField
                                    values={values}
                                    touched={touched}
                                    getError={getError}
                                    getWarning={getWarning}
                                />

                                <div className="row">
                                    <div className="col-12">
                                        <div className="mb16">
                                            <label className="fw500" data-testid={`${testIdPrefix}.dob-label`}>
                                                Date of Birth {` `}
                                                <a
                                                    className="link clickable"
                                                    onClick={(ev) => { togglePopup(ev, PopupTypes.DATE_OF_BIRTH); }}
                                                    data-testid={`${testIdPrefix}.dob-info-link`}
                                                >
                                                    <img src="/img/v4/info-icon.svg" width={12} alt="info" data-testid={testIds.dobImg} />
                                                </a>
                                            </label>
                                        </div>
                                        <div className="row">
                                            <div className="col-5 col-md-6">
                                                <Select
                                                    id="month"
                                                    field="month"
                                                    options={months}
                                                    validate={createReactFormValidator(required)}
                                                    optionsAsPrimitiveArray={true}
                                                    onChange={(val) => handleMonthChange(val)}
                                                    themeV3
                                                    label="Month"
                                                    data-testid={`${testIdPrefix}.month-select`}
                                                />
                                            </div>
                                            <div className="col-3 col-md-3">
                                                <Select
                                                    id="day"
                                                    field="day"
                                                    options={dayValues}
                                                    validate={createReactFormValidator(required)}
                                                    optionsAsPrimitiveArray={true}
                                                    onChange={(val) => handleDayChange(val)}
                                                    themeV3
                                                    label="Day"
                                                    data-testid={`${testIdPrefix}.day-select`}
                                                />
                                            </div>
                                            <div className="col-4 col-md-3">
                                                <Select
                                                    id="year"
                                                    field="year"
                                                    options={years}
                                                    validate={createReactFormValidator(required)}
                                                    optionsAsPrimitiveArray={true}
                                                    onChange={(val) => handleYearChange(val)}
                                                    themeV3
                                                    label="Year"
                                                    data-testid={`${testIdPrefix}.year-select`}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="fs12 mb8">
                                    <a
                                        className="link clickable"
                                        onClick={(ev) => { togglePopup(ev, PopupTypes.BIRTHDAY); }}
                                        data-testid={`${testIdPrefix}.birthday-info-link`}
                                    >
                                        Why do I need to provide my birthday?
                                    </a>
                                </div>
                                <div className="fs12 mb16" data-testid={testIds.parentsNotice}>
                                    Are you a parent signing up for a child?{'\u00a0'}
                                    <a
                                        className="link clickable"
                                        onClick={(ev) => { togglePopup(ev, PopupTypes.PARENTS); }}
                                        data-testid={`${testIdPrefix}.parents-info-link`}
                                    >
                                        Click here.
                                    </a>
                                </div>

                                {belowAge && (
                                    <div className="fs12 mb16" data-testid={`${testIdPrefix}.below-age-notice`}>
                                        By registering for UTR Sports, you agree to our{' '}
                                        <a href={`${config.marketingAppHost}/tos`} target="_blank" rel="noopener noreferrer" data-testid={`${testIdPrefix}.below-age-tos-link`}>Terms of Service</a> and{' '}
                                        <a href={`${config.marketingAppHost}/privacy`} target="_blank" rel="noopener noreferrer" data-testid={`${testIdPrefix}.below-age-privacy-link`}>Privacy Policy</a>.
                                        {' '}If you are a parent entering personally identifiable information for players under age 13, you confirm that you are responsible for managing the profile. As a reminder, we do not permit children under 13 to register with UTR Sports or provide us with any personal information.
                                    </div>
                                )}

                                <label className={styles.termsLabel} data-testid={`${testIdPrefix}.terms-label`}>
                                    <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={handleOnTermsCheckChange}
                                        data-testid={`${testIdPrefix}.terms-checkbox`}
                                    />I agree to the UTR <a href={`${config.marketingAppHost}/tos`} target="_blank" rel="noopener noreferrer" data-testid={`${testIdPrefix}.terms-tos-link`}>Terms of Service</a>
                                </label>

                                <button
                                    type="submit"
                                    className={`btn btn-primary btn-xl btn-block`}
                                    disabled={!checked || submitting}
                                    data-testid={`${testIdPrefix}.submit-btn`}
                                >
                                    {submitting ? 'LOADING...' : 'Confirm Profile'}
                                </button>
                            </form>
                        );
                    }}
                </Form>
                {showPopup
                    ? (
                        <Popup
                            popUpType={popUpType}
                            closePopup={togglePopup}
                        />
                    )
                    : null}
            </div>
        </div>
    );
};

OAuthConfirmationPage.propTypes = {
    providerConfig: PropTypes.shape({
        testIdPrefix: PropTypes.string.isRequired,
        getUserProfile: PropTypes.func.isRequired,
        confirmUser: PropTypes.func.isRequired,
        idFieldName: PropTypes.string.isRequired,
        testIds: PropTypes.shape({
            guardianSpan: PropTypes.string.isRequired,
            playerDetailsImg: PropTypes.string.isRequired,
            dobImg: PropTypes.string.isRequired,
            location: PropTypes.string.isRequired,
            parentsNotice: PropTypes.string.isRequired,
        }).isRequired,
    }).isRequired,
};

export default OAuthConfirmationPage;
